import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useMap } from "react-leaflet";
import { useSearchParams } from "react-router-dom";
import { message } from "antd";
import L from "leaflet";
import { GPS, TrapSite } from "../../../types";
import { useLazyGeocodeReverseQuery } from "../../../actions/trapsiteInQuery";
import {
  toLatLng,
  haversineMeters,
  DRIFT_THRESHOLD_M,
} from "../../../utils/geo";
import { useTrapsiteEditorContext } from "./TrapsiteEditorContext";
import MapContextMenuController from "./MapContextMenuController";
import MapContextMenu from "./MapContextMenu";
import DraftMarker from "./DraftMarker";
import EditorMarkers from "./EditorMarkers";
import TrapEditorPanel from "./TrapEditorPanel";
import TrapsiteEditorPanel from "./TrapsiteEditorPanel";
import { EditorDevice, TrapsiteDraft } from "./types";
import { distanceToMeters } from "../../../utils/geo";
import { computeGhosts } from "./ghosts";
import { EDIT_TRAP_PARAM } from "./openTrapEditor";
import {
  PANEL_GUTTER_PX,
  PANEL_WIDTH_PX,
  PIN_ZOOM,
  POPUP_Z_INDEX,
} from "./constants";
import "./index.css";

interface Props {
  trapSites: Partial<TrapSite>[];
  devices: EditorDevice[];
  /** Host element for the panel and menu; a sibling of the Leaflet container. */
  container: HTMLElement | null;
  /** Whether this map offers creating a collection location. */
  allowCreateTrapsite: boolean;
  /** The location this map is scoped to, if any. */
  scopedTrapSiteId?: string | null;
}

/**
 * The single child MapComponent adds inside <MapContainer> when editing is on.
 *
 * Everything it renders is either a null-returning map listener or a Leaflet
 * layer; the panel and the menu are portalled out to `container`, which is a
 * sibling of the map rather than a child of it, so html2canvas skips them and
 * antd popups stay reachable.
 */
const TrapsiteEditorLayer: React.FC<Props> = ({
  trapSites,
  devices,
  container,
  allowCreateTrapsite,
  scopedTrapSiteId = null,
}) => {
  const map = useMap();
  const editor = useTrapsiteEditorContext();
  const [runReverse] = useLazyGeocodeReverseQuery();
  // Scoped rather than the global `message`, which renders at document.body
  // and is therefore invisible whenever the map is in pseudo-fullscreen. The
  // holder lives in the wrapper so it survives the panel closing on save.
  const [messageApi, messageHolder] = message.useMessage();

  // The draft as it was when the panel opened. A ref rather than state so a
  // background device refetch cannot move the goalposts mid-edit and make the
  // dirty flag flicker or swallow the user's changes.
  const initialRef = useRef<TrapsiteDraft | null>(null);
  const openKey = `${editor.target}:${editor.mode}:${editor.editingId}`;
  const lastOpenKey = useRef<string | null>(null);

  if (editor.draft && lastOpenKey.current !== openKey) {
    lastOpenKey.current = openKey;
    initialRef.current = editor.draft;
  }
  if (!editor.draft && lastOpenKey.current !== null) {
    lastOpenKey.current = null;
    initialRef.current = null;
  }

  // --- Pseudo-fullscreen -----------------------------------------------
  // The panel needs room the home map does not have. leaflet.fullscreen is
  // configured for pseudo-fullscreen (a CSS class, not the native API) in
  // MapExpand, which is what lets the panel be an ordinary sibling and keeps
  // document.body portals - antd popups and message toasts - usable.
  const enteredFullscreen = useRef(false);
  const anyEditorOpen = !!editor.draft || !!editor.deviceDraft;

  useEffect(() => {
    const anyMap = map as any;
    if (anyEditorOpen) {
      if (!anyMap._isFullscreen) {
        anyMap.toggleFullscreen?.();
        enteredFullscreen.current = true;
      }
    } else if (enteredFullscreen.current) {
      if (anyMap._isFullscreen) anyMap.toggleFullscreen?.();
      enteredFullscreen.current = false;
    }
  }, [map, anyEditorOpen]);

  // Leave the editor if the user exits fullscreen by other means, rather than
  // stranding the panel over a map that has shrunk back to a corner.
  useEffect(() => {
    const onExit = () => {
      enteredFullscreen.current = false;
    };
    map.on("exitFullscreen", onExit);
    return () => {
      map.off("exitFullscreen", onExit);
    };
  }, [map]);

  // --- What else is on the map while editing --------------------------------
  // Editing a trap shows the location it belongs to; editing a location shows
  // its traps. Either way the relationship being edited is visible, and every
  // pin is draggable.
  const companionSites = useMemo(() => {
    if (editor.deviceDraft?.trapSiteId) {
      const site = trapSites.find(
        (t) => t._id === editor.deviceDraft!.trapSiteId,
      );
      return site ? [site] : [];
    }
    return [];
  }, [editor.deviceDraft, trapSites]);

  const companionDevices = useMemo(() => {
    if (editor.draft && editor.editingId) {
      return devices.filter((d) => editor.draft!.deviceIds.includes(d.uuid));
    }
    if (editor.draft) {
      return devices.filter((d) => editor.draft!.deviceIds.includes(d.uuid));
    }
    return [];
  }, [editor.draft, editor.editingId, devices]);

  // --- Framing ----------------------------------------------------------
  /**
   * Bring a point into view, keeping it clear of the panel.
   *
   * Used both when the editor opens and when the pin jumps somewhere the user
   * did not drag it to - snapping to a geocoded address can move it miles, and
   * leaving the map behind would strand the pin off-screen.
   */
  const frameOnPoint = useCallback(
    (gps: GPS) => {
      const point = toLatLng(gps);
      map.flyToBounds(L.latLngBounds([point, point]), {
        paddingBottomRight: [PANEL_WIDTH_PX + PANEL_GUTTER_PX, 0],
        maxZoom: PIN_ZOOM,
      });
    },
    [map],
  );

  const ghosts = useMemo(
    () =>
      computeGhosts({
        devices,
        trapSites,
        staged: editor.staged,
        editedDeviceUuid: editor.deviceDraft?.deviceUuid,
        editedDeviceGps: editor.deviceDraft?.gps,
        editedSiteId:
          editor.editingId && editor.draft ? editor.editingId : null,
        editedSiteGps: editor.draft?.gps,
      }),
    [
      devices,
      trapSites,
      editor.staged,
      editor.deviceDraft,
      editor.draft,
      editor.editingId,
    ],
  );

  /**
   * Bumped when a PIN finishes moving, so the view refits once the drag ends
   * rather than fighting the user during it.
   *
   * Driven from the markers rather than the map's own dragend, which fires on
   * panning - refitting on that would undo the pan immediately.
   */
  const [moveToken, setMoveToken] = useState(0);
  const handlePinMoveEnd = useCallback(() => setMoveToken((n) => n + 1), []);

  /**
   * Everything the editor is currently showing, so the view can hold all of it.
   */
  const shownPoints = useMemo((): GPS[] => {
    const primary = editor.draft?.gps ?? editor.deviceDraft?.gps;
    if (!primary) return [];
    return [
      primary,
      ...companionSites
        .map((s) => editor.staged.trapSites[s._id!] ?? s.gps)
        .filter((g): g is GPS => !!g),
      ...companionDevices
        .map((d) => editor.staged.devices[d.uuid] ?? d.gps)
        .filter((g): g is GPS => !!g),
    ];
  }, [
    editor.draft,
    editor.deviceDraft,
    editor.staged,
    companionSites,
    companionDevices,
  ]);

  /**
   * Identity of the set on screen - which things, not where they are.
   *
   * Refitting is keyed on this rather than on the positions, which is what
   * makes the view behave: changing the trap or its location reframes
   * everything, while panning, zooming and dragging a pin leave the view
   * exactly where the user put it.
   */
  const shownKey = useMemo(
    () =>
      [
        editor.target,
        editor.editingId,
        editor.deviceDraft?.deviceUuid,
        editor.deviceDraft?.trapSiteId,
        companionDevices.map((d) => d.uuid).join(","),
        companionSites.map((s) => s._id).join(","),
        // A finished pin drag counts as a change of arrangement.
        moveToken,
      ].join("|"),
    [
      editor.target,
      editor.editingId,
      editor.deviceDraft,
      companionDevices,
      companionSites,
      moveToken,
    ],
  );

  const framedFor = useRef<string | null>(null);
  useEffect(() => {
    if (shownPoints.length === 0) {
      framedFor.current = null;
      return;
    }
    if (framedFor.current === shownKey) return;
    framedFor.current = shownKey;

    if (shownPoints.length === 1) {
      frameOnPoint(shownPoints[0]);
      return;
    }
    map.flyToBounds(L.latLngBounds(shownPoints.map(toLatLng)), {
      paddingBottomRight: [PANEL_WIDTH_PX + PANEL_GUTTER_PX, 0],
      paddingTopLeft: [PANEL_GUTTER_PX, PANEL_GUTTER_PX],
      maxZoom: PIN_ZOOM,
    });
    // shownPoints is deliberately absent: refitting is keyed on the SET
    // changing, not on any pin moving within it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shownKey, map, frameOnPoint]);

  // The map's usable width changes when the panel appears, and the fullscreen
  // control only invalidates for its own toggle.
  useEffect(() => {
    const id = setTimeout(() => map.invalidateSize(), 250);
    return () => clearTimeout(id);
  }, [map, anyEditorOpen]);

  // --- Create: prefill the address by reverse-geocoding the click ---------
  const prefilled = useRef(false);
  useEffect(() => {
    if (editor.mode !== "create" || !editor.draft) {
      prefilled.current = false;
      return;
    }
    // Once per open. The pin moves freely afterwards, and re-deriving the
    // address from it on every drag is exactly what the design forbids.
    if (prefilled.current) return;
    prefilled.current = true;

    const gps: GPS = editor.draft.gps;
    runReverse(gps)
      .unwrap()
      .then((result) => {
        if (!result?.item) return;
        editor.patchDraft({
          address: result.item.address,
          details: result.item.details,
          geocodedGps: result.item.gps,
        });
      })
      .catch(() => {
        // Leaving the address blank is a recoverable state - the user types
        // one and presses Find - so this must not interrupt them. Swallowed
        // rather than surfaced because the panel has only just opened.
      });
  }, [editor, runReverse]);

  const handleEditTrapsite = useCallback(
    (id: string) => {
      const site = trapSites.find((t) => t._id === id);
      if (site) editor.startEdit(site);
    },
    [trapSites, editor],
  );

  const handleEditTrap = useCallback(
    (uuid: string) => {
      const device = devices.find((d) => d.uuid === uuid);
      if (device) editor.startEditTrap(device);
    },
    [devices, editor],
  );

  /**
   * Open the editor for a trap named in the URL.
   *
   * The tables that offer "Edit Trap" are not always on a page with a map, so
   * the request arrives as a query parameter. Consumed once and stripped, so a
   * refresh or a later close does not reopen it.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTrap = searchParams.get(EDIT_TRAP_PARAM);
  useEffect(() => {
    if (!requestedTrap || !editor.enabled) return;
    // Devices arrive asynchronously; wait rather than dropping the request.
    const device = devices.find((d) => d.uuid === requestedTrap);
    if (!device) return;

    editor.startEditTrap(device);
    const next = new URLSearchParams(searchParams);
    next.delete(EDIT_TRAP_PARAM);
    setSearchParams(next, { replace: true });
  }, [requestedTrap, devices, editor, searchParams, setSearchParams]);

  const drift = useMemo(() => {
    if (!editor.draft?.geocodedGps) return { drifted: false };
    return {
      drifted:
        haversineMeters(editor.draft.gps, editor.draft.geocodedGps) >
        DRIFT_THRESHOLD_M,
    };
  }, [editor.draft]);

  const primaryGps = editor.draft?.gps ?? editor.deviceDraft?.gps ?? null;

  return (
    <>
      {/* Leaflet layers and listeners; these belong inside the map. */}
      <MapContextMenuController />
      {primaryGps && (
        <EditorMarkers
          primary={primaryGps}
          onMovePrimary={editor.movePinTo}
          onMoveEnd={handlePinMoveEnd}
          devices={companionDevices}
          trapSites={companionSites}
          // Every location's boundary, so widening a radius or dragging a
          // location has a neighbour to visibly run into.
          contextSites={trapSites}
          primaryTrapSiteId={editor.draft ? editor.editingId : null}
          ghosts={ghosts}
          // Where the edited pin was recorded, so its drag line always
          // measures from there rather than from the last drop.
          primaryOrigin={
            editor.deviceDraft?.deviceUuid
              ? (devices.find((d) => d.uuid === editor.deviceDraft!.deviceUuid)
                  ?.gps ?? null)
              : editor.editingId
                ? (trapSites.find((t) => t._id === editor.editingId)?.gps ??
                  null)
                : null
          }
          primaryLabel={
            editor.deviceDraft
              ? editor.deviceDraft.displayName || "New trap position"
              : editor.draft?.displayName || "New collection location"
          }
          // A location's own boundary follows the live draft radius, so
          // editing the number redraws the circle immediately.
          primaryRadiusM={
            editor.draft
              ? distanceToMeters(
                  editor.draft.radiusValue,
                  editor.draft.radiusUnit,
                )
              : null
          }
        />
      )}
      {editor.draft && (
        <DraftMarker
          geocodedGps={editor.draft.geocodedGps}
          drifted={drift.drifted}
        />
      )}

      {/* Chrome. Portalled to the wrapper rather than left as MapContainer
          children, which would render it inside .leaflet-container - the exact
          element MapDownloadControl hands to html2canvas. Outside it, the
          panel and menu stay out of the PNG with no opt-out attribute, while
          the pins (real layers) still appear. */}
      {container &&
        createPortal(
          <>
            {/* Lifted above the panel: antd bases popups at z-index 1000 and
                the panel sits at 1200, so an in-place toast would render
                behind it. */}
            <div style={{ position: "relative", zIndex: POPUP_Z_INDEX }}>
              {messageHolder}
            </div>
            <MapContextMenu
              onEditTrapsite={handleEditTrapsite}
              onEditTrap={handleEditTrap}
              allowCreateTrapsite={allowCreateTrapsite}
              scopedTrapSiteId={scopedTrapSiteId}
              container={container}
            />
            {editor.draft && initialRef.current && (
              <TrapsiteEditorPanel
                initial={initialRef.current}
                devices={devices}
                trapSites={trapSites}
                container={container}
                messageApi={messageApi}
                onFramePoint={frameOnPoint}
              />
            )}
            {editor.deviceDraft && (
              <TrapEditorPanel
                devices={devices}
                trapSites={trapSites}
                container={container}
                messageApi={messageApi}
                onFramePoint={frameOnPoint}
              />
            )}
          </>,
          container,
        )}
    </>
  );
};

export default TrapsiteEditorLayer;
